import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

export default function DeckHotTubStructuralReality() {
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Consolidated Schema Data for Hot Tub Structural Reality Blog
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": "Can Your Deck Actually Support a Hot Tub? The Structural Reality",
        "description": "No, a standard residential deck cannot safely support a filled hot tub. Learn the structural physics, framing mechanics, and building code requirements.",
        "author": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "url": "https://busybeaverconstruction.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "logo": {
            "@type": "ImageObject",
            "url": "https://busybeaverconstruction.com/assets/images/2020/03/busy-beaver-logo.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://busybeaverconstruction.com/blog/can-your-deck-support-hot-tub"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://busybeaverconstruction.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Can Your Deck Support a Hot Tub?",
            "item": "https://busybeaverconstruction.com/blog/can-your-deck-support-hot-tub"
          }
        ]
      }
    ]
  }

  const sidebarImages = [
    { src: "/assets/images/Decks/Composite Decks/Outdoor-hot-tub-area-with-covered-lounging-space.jpg", alt: "Deck with hot tub Calgary" },
    { src: "/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg", alt: "Ground level deck Calgary" },
    { src: "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg", alt: "Multi-level deck Calgary" },
    { src: "/assets/images/Decks/Composite Decks/Timbertech-Terrain-deck-in-Brown-Oak-trim-and-Sandy-Birch-surface.jpg", alt: "TimberTech Terrain composite deck Calgary" },
    { src: "/assets/images/Decks/Wood Decks/Multi-level-Cedar-wood-deck.jpg", alt: "Multi-level cedar wood deck Calgary" }
  ]

  return (
    <main className="bg-gray-50">
      <Helmet>
        <title>Can Your Deck Actually Support a Hot Tub? | Busy Beaver Construction</title>
        <meta name="description" content="No, a standard residential deck cannot safely support a filled hot tub. Learn the structural physics, framing mechanics, and building code requirements." />
        <meta name="keywords" content="hot tub on deck, deck structural requirements, deck load capacity, hot tub deck framing, Calgary deck builder" />
        <link rel="canonical" href="https://busybeaverconstruction.com/blog/can-your-deck-support-hot-tub" />
        <meta property="og:title" content="Can Your Deck Actually Support a Hot Tub? The Structural Reality" />
        <meta property="og:description" content="No, a standard residential deck cannot safely support a filled hot tub. Learn the structural physics, framing mechanics, and building code requirements." />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/Outdoor-hot-tub-area-with-covered-lounging-space.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Decks/Composite Decks/Outdoor-hot-tub-area-with-covered-lounging-space.jpg" as="image" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />

      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/55 z-10"></div>
        <img
          src="/assets/images/Decks/Composite Decks/Outdoor-hot-tub-area-with-covered-lounging-space.jpg"
          alt="Outdoor hot tub integrated into a custom deck in Calgary"
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
        />
        <div className="relative z-20 mt-32 text-center max-w-4xl mx-auto px-4">
          <div className="inline-block bg-beaver-green rounded py-2 px-6 mb-6">
            <p className="text-black text-sm font-bold tracking-wider uppercase">Structural Guide</p>
          </div>
          <h1 className="text-white text-5xl lg:text-7xl font-primary drop-shadow-lg mb-4 leading-tight">
            Can Your Deck Actually Support a Hot Tub?
          </h1>
          <p className="text-white/90 text-lg font-medium font-accent mt-4">
            The Structural Reality
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

            {/* Article */}
            <article className="lg:col-span-8 space-y-8 text-lg text-gray-700 leading-relaxed font-text">

              <p className="text-xl text-gray-900 leading-relaxed font-bold">
                No, a standard residential deck cannot safely support a filled hot tub. Most backyard decks accommodate general foot traffic and outdoor patio furniture, not several tons of concentrated water weight. Placing a spa on an unreinforced deck invites catastrophic structural failure, property damage, and severe personal injury.
              </p>

              <p>
                Before you purchase a spa, you must understand the underlying structural physics, framing mechanics, and building code requirements. This guide breaks down the engineering reality of hot tub loads and shows you how to design a safe, long-lasting installation.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                The Weight Reality: Breaking Down the Math Behind a Hot Tub
              </h2>
              
              <p>
                A filled hot tub weighs significantly more than most homeowners realize. Water creates immense physical density within a very compact surface footprint. Calculating the total operating weight reveals why standard framing members cannot handle the stress.
              </p>

              <p>
                To determine the actual load your outdoor structure must hold, calculate three distinct variables:
              </p>
              
              <ul className="list-disc pl-6 space-y-4 marker:text-beaver-green">
                <li>
                  <strong>Dry Weight (The Spa Shell):</strong> An empty four-person to six-person acrylic spa weighs between 600 and 1,000 pounds.
                </li>
                <li>
                  <strong>Water Weight:</strong> Water weighs 8.34 pounds per gallon. A typical mid-sized hot tub holds 350 to 450 gallons of water. That water adds 2,900 to 3,750 pounds of pure liquid mass.
                </li>
                <li>
                  <strong>Occupant Weight:</strong> Four to six adults add another 700 to 1,200 pounds of live load.
                </li>
              </ul>

              <p>
                When you add these three factors together, a modest family hot tub weighs between 4,500 and 6,000 pounds. Larger eight-person models often exceed 7,500 pounds. That total load equals the weight of a full-size pickup truck sitting in an eight-foot by eight-foot area of your deck.
              </p>

              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-beaver-green my-8 shadow-sm">
                <p className="font-bold text-gray-900 mb-2">Total Weight Calculation Example (Mid-Sized 400-Gallon Spa):</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Empty Tub Shell: 800 lbs</li>
                  <li>Water (400 × 8.34): 3,336 lbs</li>
                  <li>4 Bathers: 750 lbs</li>
                  <li><strong>Total Weight: 4,886 lbs</strong></li>
                </ul>
              </div>

              <p>
                Dividing that 4,886-pound total across a standard 64-square-foot footprint (8 feet by 8 feet) produces a concentrated load of roughly 76 to 100 pounds per square foot (PSF). If your spa features a smaller base or holds deeper water, that concentration easily surges past 120 PSF.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                Standard Residential Deck Design vs. Hot Tub Load Requirements
              </h2>

              <p>
                Standard residential decks cannot support a hot tub because building codes design them for everyday pedestrian traffic. Local regulations require frames to handle uniform crowds, not localized, heavy machinery. Comparing standard design loads against spa weights demonstrates this dangerous mismatch.
              </p>

              <p>
                Building codes, including the International Residential Code (IRC Section R507), establish clear load criteria for outdoor residential platforms:
              </p>

              <ul className="list-disc pl-6 space-y-4 marker:text-beaver-green">
                <li>
                  <strong>Uniform Live Load:</strong> 40 PSF (people, patio chairs, temporary items)
                </li>
                <li>
                  <strong>Uniform Dead Load:</strong> 10 PSF (the weight of the framing lumber, fasteners, and decking boards)
                </li>
                <li>
                  <strong>Total Design Capacity:</strong> 50 PSF
                </li>
              </ul>

              <p>
                A standard deck provides 50 PSF of total structural capacity. In contrast, a filled hot tub delivers a concentrated load ranging from 100 to 150 PSF. The spa exceeds standard residential framing capacity by two to three times.
              </p>

              <p>
                Standard deck frames rely on 2x8 or 2x10 joists spaced 16 inches on-center spanning 10 to 12 feet. Under standard loads, these boards flex within acceptable limits (typically an L/360 deflection ratio, which means the joist deflects no more than 1/360th of its span). When you place a 5,000-pound spa on those same joists, the concentrated load exceeds their structural bending strength. The joists bow, the fasteners shear, and the substructure fails.
              </p>

              <p>
                If you want to evaluate how different layouts and timber specifications influence baseline framing strength and project budgets, use an online <a href="/estimate" onClick={(e) => handleNavigation(e, '/estimate')} className="text-beaver-green font-semibold hover:underline">Deck Cost Calculator</a> to test your parameters.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                Critical Failure Points When You Put a Hot Tub on an Unreinforced Deck
              </h2>

              <p>
                An overloaded deck rarely breaks in the center of a board first. Instead, structural failure strikes mechanical connections, support posts, and the house envelope. Identifying these weak links explains why proper reinforcement matters.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                1. Ledger Board Separation
              </h3>
              <p>
                The ledger board fastens your deck frame directly to the rim joist of your house. When you place a hot tub near the house wall, immense downward shear force pulls on the ledger fasteners. Standard lag screws or through-bolts can snap, or the house rim joist can tear away entirely. Ledger detachment causes the vast majority of catastrophic deck collapses.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                2. Post-to-Beam Connection Shear
              </h3>
              <p>
                Older or poorly constructed decks often feature beams bolted to the sides of 4x4 or 6x6 support posts using carriage bolts. In this configuration, smooth steel bolts carry the entire load through shear resistance. A hot tub will shear those bolts or crush the wood fibers around the bolt holes. Modern structural engineering demands that beams bear directly on top of notched 6x6 posts or rest inside approved post-cap metal brackets.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                3. Joist Deflection and Cracking
              </h3>
              <p>
                Standard 2x8 joists spaced 16 inches on-center cannot span across beams while supporting a hot tub. The center of the joists will sag significantly beyond the allowable deflection limit. This deflection causes internal grain fractures, splits the wood around joist hanger nails, and warps the surface decking boards.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                4. Footing Punching and Soil Subsidence
              </h3>
              <p>
                Standard concrete deck footings spread a 50 PSF load across undisturbed soil. A 5,000-pound hot tub concentrates thousands of pounds of downward thrust onto two or three individual footings. If those concrete piers lack adequate diameter or depth, the concentrated weight punches them into the ground, tilting the entire frame.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                How to Engineer Deck Framing for a Hot Tub
              </h2>

              <p>
                You can build an elevated deck that safely holds a hot tub, but it requires dedicated structural engineering. Framing for a spa demands a completely different design approach than framing for standard patio furniture. You must build a direct, continuous load path from the tub base down to the earth.
              </p>

              <p>
                To engineer an elevated deck frame for a spa, professional builders implement several structural modifications:
              </p>

              <ul className="list-disc pl-6 space-y-4 marker:text-beaver-green">
                <li>
                  <strong>Tighter Joist Spacing:</strong> Reduce joist spacing beneath the spa footprint from 16 inches on-center down to 12 inches, 10 inches, or 8 inches on-center.
                </li>
                <li>
                  <strong>Doubled or Tripled Joists:</strong> Sister multiple structural framing boards together directly under the edges and center of the hot tub.
                </li>
                <li>
                  <strong>Mid-Span Drop Beams:</strong> Install dedicated multi-ply beams (typically doubled or tripled 2x10 or 2x12 pressure-treated dimensional lumber) directly below the tub footprint to cut joist spans in half.
                </li>
                <li>
                  <strong>Heavy-Duty Support Columns:</strong> Place 6x6 support posts directly under the hot tub beams to transfer weight vertically. Never rely on the house ledger to carry the hot tub load.
                </li>
                <li>
                  <strong>Oversized Concrete Footings:</strong> Pour larger, thicker concrete piers (often 18 to 24 inches in diameter) below local frost lines to prevent soil compression and frost heave.
                </li>
                <li>
                  <strong>Structural Hardware:</strong> Secure every connection using heavy-gauge metal connectors, such as Simpson Strong-Tie post caps, heavy joist hangers, and structural screws.
                </li>
              </ul>

              <p>
                Reviewing a comprehensive guide on <a href="/blog/types-of-decks-calgary" onClick={(e) => handleNavigation(e, '/blog/types-of-decks-calgary')} className="text-beaver-green font-semibold hover:underline">types of decks and framing layouts</a> will help you understand how multi-level frames distribute these loads.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                The Three Best Architectural Solutions for Installing a Hot Tub
              </h2>

              <p>
                Homeowners have three primary methods for integrating a hot tub into a backyard deck design. Each solution offers distinct structural benefits, construction costs, and accessibility advantages. Choose the option that best matches your yard topography and budget.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Option 1: The Ground-Level Concrete Slab (The Cut-Out Method)
              </h3>
              <p>
                This configuration represents the most reliable, cost-effective, and popular choice for homeowners.
              </p>
              <p>
                You pour a 4-inch to 6-inch steel-reinforced concrete pad directly on compacted gravel on the ground. The hot tub sits on this concrete pad. You then build your deck around the hot tub, leaving a structural cut-out with a 1-inch to 2-inch clearance gap.
              </p>
              <p>
                The deck carries only foot traffic, while the solid ground absorbs 100 percent of the water weight. This design keeps the hot tub rim roughly 16 to 20 inches above the deck surface, creating an easy step-in height.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Option 2: The Fully Engineered Elevated Deck Frame
              </h3>
              <p>
                If you own a walk-out basement or want your spa on a second-story deck, you must build an elevated, heavily reinforced timber structure.
              </p>
              <p>
                This approach requires an independent structural support grid built inside the main deck frame. You install dedicated footings, 6x6 posts, heavy beams, and 8-inch to 10-inch joist spacing directly under the tub. You isolate this heavy sub-frame from the house ledger so that the hot tub weight travels straight into the ground.
              </p>
              <p>
                While visually striking, this option costs significantly more because of the extra lumber, concrete, and engineering requirements. You can review current <a href="/blog/5" onClick={(e) => handleNavigation(e, '/blog/5')} className="text-beaver-green font-semibold hover:underline">deck construction costs</a> to see how heavy structural engineering impacts labor and material pricing.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-3 font-secondary">
                Option 3: The Recessed Vault Installation
              </h3>
              <p>
                A recessed vault positions the hot tub inside a lowered pit, making the top of the spa flush with the deck boards.
              </p>
              <p>
                To execute this build, contractors pour a sunken concrete vault with dedicated retaining walls, gravity drainage, and an integrated sump pump. The deck frames over the edges of the vault while the spa sits on the concrete floor.
              </p>
              <p>
                You must maintain a 24-inch to 36-inch clearance around the spa cabinet panels inside the vault. Without removable trapdoors or perimeter clearance, technicians cannot service pumps, heaters, or plumbing lines when leaks occur.
              </p>
              <p>
                To enhance your outdoor space and protect your spa area from wind and sightlines, many homeowners combine these deck layouts with custom privacy walls.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                Permitting, Codes, and Structural Engineering Inspections
              </h2>

              <p>
                Installing a hot tub on or adjacent to a deck always requires municipal building permits. Local building departments treat hot tubs as major structural and electrical additions. Skipping the permitting process creates serious financial and legal liabilities.
              </p>

              <p>
                Building officials verify several critical elements before approving your installation:
              </p>

              <ul className="list-disc pl-6 space-y-4 marker:text-beaver-green">
                <li>
                  <strong>Frost Line Depth:</strong> Inspectors require concrete footings to extend below the regional frost line (often 3 to 4 feet deep in northern climates) to eliminate frost heave risks.
                </li>
                <li>
                  <strong>Electrical Safety:</strong> Spas require dedicated 240V, 50-amp to 60-amp GFCI disconnect panels located at least 5 feet away from the water edge to prevent electrocution hazards.
                </li>
                <li>
                  <strong>Barrier and Safety Codes:</strong> Most jurisdictions mandate 48-inch self-closing, self-latching safety gates or lockable spa safety covers to prevent accidental drownings.
                </li>
                <li>
                  <strong>Engineering Review:</strong> When placing a spa on an elevated platform, municipalities often require stamped structural drawings from a licensed engineer.
                </li>
              </ul>

              <p>
                If you skip building permits, you risk severe penalties. Municipalities can issue stop-work orders or force you to dismantle the structure. Furthermore, if an unpermitted deck collapses under a hot tub, your homeowner insurance policy will likely deny the property damage claim. Reviewing regional <a href="/blog/4" onClick={(e) => handleNavigation(e, '/blog/4')} className="text-beaver-green font-semibold hover:underline">deck permit requirements</a> ensures that your project complies with municipal bylaws from the start.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 font-secondary border-b pb-4">
                How to Inspect an Existing Deck Before Planning a Hot Tub
              </h2>

              <p>
                Never place a hot tub on an existing deck without conducting a thorough structural audit. Even well-built decks degrade over time from weather exposure, rot, and fastener fatigue. A rigorous inspection helps you determine whether to retrofit your existing frame or start fresh.
              </p>

              <p>
                Follow this practical checklist when evaluating your current deck:
              </p>

              <ul className="list-disc pl-6 space-y-4 marker:text-beaver-green">
                <li>
                  <strong>Check the Ledger Connection:</strong> Inspect the attachment point at the house wall. Ensure the builder used structural screws or half-inch lag bolts with proper flashing, not common framing nails.
                </li>
                <li>
                  <strong>Examine Wood Soundness:</strong> Probe joists, ledger boards, and post bottoms with a screwdriver. Soft, spongy wood indicates fungal rot and structural decay.
                </li>
                <li>
                  <strong>Assess Post Sizing and Bracing:</strong> Confirm whether your deck uses 6x6 posts rather than older 4x4 posts. Look for diagonal Y-bracing that prevents lateral sway and racking.
                </li>
                <li>
                  <strong>Inspect Hardware Condition:</strong> Look for rusted joist hangers, missing connector nails, or corroded fasteners.
                </li>
                <li>
                  <strong>Measure Joist Spans:</strong> Measure the thickness, depth, and spacing of your existing joists. If you find 2x8 joists spaced 16 inches or 24 inches on-center across a long span, the structure cannot handle additional load.
                </li>
              </ul>

              <p>
                If your deck shows signs of framing rot or undersized lumber, determine whether you need to <a href="/blog/deck-replacement-vs-repair-calgary" onClick={(e) => handleNavigation(e, '/blog/deck-replacement-vs-repair-calgary')} className="text-beaver-green font-semibold hover:underline">repair or replace your structure</a> before proceeding. For guaranteed safety and accurate load calculations, <a href="/appointment" onClick={(e) => handleNavigation(e, '/appointment')} className="text-beaver-green font-semibold hover:underline">book an onsite consultation</a> with an experienced professional builder to assess your property.
              </p>

              <div className="bg-gray-900 text-white rounded-xl p-8 my-12 shadow-xl border-l-4 border-beaver-green">
                <h2 className="text-2xl font-bold mb-4 font-secondary text-beaver-green">
                  Summary Checklist for Homeowners
                </h2>
                <ul className="list-disc pl-6 space-y-3">
                  <li>Calculate your spa's total operating weight using water (8.34 lbs/gal), dry shell weight, and bathers.</li>
                  <li>Remember that standard decks hold 50 PSF, while hot tubs exert 76 to 150+ PSF.</li>
                  <li>Never place a hot tub on standard 16-inch on-center framing without independent reinforcement.</li>
                  <li>Prefer a ground-level concrete pad with a deck cut-out for maximum safety and lowest build cost.</li>
                  <li>Always pull local building and electrical permits before beginning construction.</li>
                </ul>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a href="tel:4032865400" className="inline-flex justify-center items-center px-6 py-3 bg-white text-black font-bold rounded shadow-lg hover:bg-gray-100 transition-all font-accent">
                    Call (403) 286-5400
                  </a>
                  <a href="/appointment" onClick={(e) => handleNavigation(e, '/appointment')} className="inline-flex justify-center items-center px-6 py-3 bg-beaver-green text-black font-bold rounded shadow-lg hover:brightness-110 transition-all font-accent">
                    Book Structural Assessment
                  </a>
                </div>
              </div>

              {/* Added standard Related Reading Section */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4 font-secondary">Related Reading</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>
                    <span className="text-beaver-green font-bold mr-2">→</span>
                    <a href="/blog/5" onClick={(e) => handleNavigation(e, '/blog/5')} className="text-beaver-green font-semibold hover:underline">How much does a deck cost in Calgary?</a>
                  </li>
                  <li>
                    <span className="text-beaver-green font-bold mr-2">→</span>
                    <a href="/blog/4" onClick={(e) => handleNavigation(e, '/blog/4')} className="text-beaver-green font-semibold hover:underline">Calgary deck permit requirements</a>
                  </li>
                  <li>
                    <span className="text-beaver-green font-bold mr-2">→</span>
                    <a href="/blog/types-of-decks-calgary" onClick={(e) => handleNavigation(e, '/blog/types-of-decks-calgary')} className="text-beaver-green font-semibold hover:underline">Types of decks and framing layouts</a>
                  </li>
                  <li>
                    <span className="text-beaver-green font-bold mr-2">→</span>
                    <a href="/blog/deck-replacement-vs-repair-calgary" onClick={(e) => handleNavigation(e, '/blog/deck-replacement-vs-repair-calgary')} className="text-beaver-green font-semibold hover:underline">Deck replacement vs repair Calgary</a>
                  </li>
                </ul>
              </div>

            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold mb-6 font-secondary text-gray-900">Project Inspiration</h3>
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar">
                  {sidebarImages.map((img, index) => (
                    <div key={index} className="overflow-hidden rounded-lg h-48 lg:h-56 group shadow-sm">
                      <img
                        src={img.src}
                        alt={img.alt}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-600 mb-4 font-text">
                    25 years building Calgary decks. Free on-site estimates with no obligation.
                  </p>
                  <a href="/appointment" onClick={(e) => handleNavigation(e, '/appointment')} className="block text-center bg-beaver-green text-black font-bold py-3 px-4 rounded hover:brightness-110 transition-all font-accent">
                    Book Free Estimate
                  </a>
                  <a href="tel:4032865400" className="block text-center text-beaver-red font-bold mt-3 hover:underline font-accent">
                    (403) 286-5400
                  </a>
                </div>
              </div>
            </aside>

          </div>
        </div>
      </section>

      <Footer />
    </main>
  )
}